import { Controller } from "@hotwired/stimulus"
import { placeBlock } from "../lib/page_blocks"

// Blocks Studio placed on this page (Phelipe 2026-10-17). The layout draws
// the top and bottom ones in place; the ones anchored to a heading, a button
// or a table wait in the hidden holder and move there here — to the bottom
// of the page when the landmark isn't on the page any more.
//
// Step 2 (2026-10-19): a small pencil shows on each block on hover (only
// when the Studio chat is on the page); pressing it opens the chat with that
// block preselected, so "say hard hats and vests" or "remove it" needs no
// pointing. The chat sends the block's id with the next message.
export default class extends Controller {
  static targets = ["top", "bottom", "holder"]
  static values = { editLabel: String, lostLabel: String, lostUrl: String }

  connect() {
    const root = this.element
    const top = this.hasTopTarget ? this.topTarget : null
    const bottom = this.hasBottomTarget ? this.bottomTarget : null
    if (this.hasHolderTarget) {
      ;[...this.holderTarget.children].forEach((el) => {
        let anchor = {}
        try { anchor = JSON.parse(el.dataset.pageBlockAnchor || "{}") } catch (e) { anchor = {} }
        const used = placeBlock(el, anchor, { top, bottom, root })
        this._lost(el, anchor, used)
      })
    }
    this._onAdded = (e) => this._handle(e.target)
    root.addEventListener("page-blocks:added", this._onAdded)
    root.querySelectorAll("[data-page-block]").forEach((el) => this._handle(el))
  }

  disconnect() { this.element.removeEventListener("page-blocks:added", this._onAdded) }

  // "Lost its place" (step 2, 2026-10-19): a block anchored before/after a landmark that
  // isn't on the page any more sits at the bottom; the page says so under it and tells
  // the server once (and again when the landmark is back), so the chat knows.
  _lost(el, anchor, used) {
    const wanted = anchor?.spot
    const lost = (wanted === "before" || wanted === "after") && used !== wanted
    const was = el.dataset.pageBlockLost === "1"
    if (lost) {
      if (!el.querySelector(":scope > [data-page-block-lost-note]")) {
        const note = document.createElement("div")
        note.setAttribute("data-page-block-lost-note", "")
        note.className = "text-[12px] text-text-muted mt-1"
        note.textContent = this.lostLabelValue || "Its spot on the page is gone, so it sits here for now."
        el.appendChild(note)
      }
    }
    if (lost !== was && this.lostUrlValue && el.dataset.pageBlock && el.dataset.pageBlock !== "new") {
      el.dataset.pageBlockLost = lost ? "1" : "0"
      const token = document.querySelector('meta[name="csrf-token"]')?.content || ""
      fetch(this.lostUrlValue.replace(":id", encodeURIComponent(el.dataset.pageBlock)), {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, Accept: "application/json" },
        body: JSON.stringify({ lost })
      }).catch(() => {})
    }
  }

  // The pencil: a round button in the block's top-right corner, visible while
  // the mouse is over the block (no hover classes: the CSS build is not watched).
  _handle(el) {
    if (!el || !el.matches("[data-page-block]") || el.dataset.pageBlock === "new" || el.querySelector(":scope > [data-page-block-edit]")) return
    if (!document.getElementById("studio-bubble")) return
    const label = this.editLabelValue || "Change with Studio"
    const btn = document.createElement("button")
    btn.type = "button"
    btn.setAttribute("data-page-block-edit", el.dataset.pageBlock)
    btn.title = label
    btn.setAttribute("aria-label", label)
    btn.className = "absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-gray-300 shadow-sm items-center justify-center text-gray-500 hover:text-brand cursor-pointer hidden"
    btn.style.cssText = "display:none;position:absolute;top:-8px;right:-8px;z-index:5"
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-3.5 h-3.5" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/></svg>'
    el.style.position = el.style.position || "relative"
    el.appendChild(btn)
    const show = () => { btn.style.display = "inline-flex" }
    const hide = () => { btn.style.display = "none" }
    el.addEventListener("mouseenter", show)
    el.addEventListener("mouseleave", hide)
    el.addEventListener("focusin", show)
    el.addEventListener("focusout", hide)
    btn.addEventListener("click", (e) => {
      e.preventDefault(); e.stopPropagation()
      const c = el.cloneNode(true)
      c.querySelectorAll("[data-page-block-edit]").forEach((n) => n.remove())
      // The title and the text as two phrases, not glued ("Pencil testHover me").
      const words = [...c.children].map((n) => (n.textContent || "").replace(/\s+/g, " ").trim()).filter(Boolean).join(" — ").slice(0, 80) || (c.textContent || "").replace(/\s+/g, " ").trim().slice(0, 80)
      const kind = el.matches("span") ? "button" : el.classList.contains("bg-amber-50") ? "note" : "card"
      window.dispatchEvent(new CustomEvent("studio-chat:open", { detail: { door: "studio", block: { id: el.dataset.pageBlock, kind, words } } }))
    })
  }
}
